import React from "react";
import { Helmet } from "react-helmet-async";
import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const NIS2 = () => {
  // No special scroll behavior needed
  
  return (
    <div className="relative min-h-screen">
      <Helmet>
        <title>NIS2 Compliance | Verdea AI</title>
        <meta name="description" content="Network and Information Security Directive 2 compliance solutions powered by AI." />
      </Helmet>
      
      {/* Background Elements */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 right-0 w-96 h-96 blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.4) 0%, rgba(16, 185, 129, 0) 70%)',
            transform: 'translate(30%, -30%)'
          }}
        ></div>
        <div
          className="absolute bottom-0 left-0 w-96 h-96 blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0) 70%)',
            transform: 'translate(-30%, 30%)'
          }}
        ></div>
      </div>
      
      {/* Navbar */}
      <header className="sticky top-0 z-50">
        <NavBar />
      </header>
      
      <main>
        {/* Hero section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-12"
              >
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  NIS2 Compliance
                  <span className="block text-2xl md:text-3xl text-emerald-600 mt-2">
                    Enhance Your Cybersecurity Framework
                  </span>
                </h1>
                <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                  Our AI-powered platform streamlines NIS2 compliance, enabling organizations to build robust cybersecurity frameworks and meet their regulatory obligations.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                    Request Demo
                  </Button>
                  <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                    Learn More
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        
        {/* Main content section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-3xl font-semibold mb-6">NIS2 Made Simple</h2>
                  <p className="text-lg text-gray-600 mb-6">
                    The Network and Information Security Directive 2 (NIS2) strengthens cybersecurity requirements across the EU, impacting essential and important entities in critical sectors. Our platform transforms complex security requirements into manageable workflows.
                  </p>
                  <ul className="space-y-3">
                    {[
                      "Comprehensive risk management assessment and documentation",
                      "Automated security measure implementation tracking",
                      "Incident reporting workflow and templates",
                      "Supply chain security assessment tools",
                      "Compliance reporting and regulatory submission preparation"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-5 h-5 text-emerald-500 mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-lg p-1 overflow-hidden"
                >
                  <div className="aspect-video bg-gray-100 rounded-lg">
                    <img 
                      src="/images/frameworks/nis2.svg" 
                      alt="NIS2 Dashboard" 
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                </motion.div>
              </div>
              
              {/* Key features */}
              <div className="mb-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="text-center mb-12"
                >
                  <h2 className="text-3xl font-semibold mb-4">Key NIS2 Features</h2>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    Our platform addresses all aspects of NIS2 compliance with sophisticated AI tools and intuitive workflows.
                  </p>
                </motion.div>
                
                <div className="grid md:grid-cols-3 gap-8">
                  {[
                    {
                      title: "Risk Assessment",
                      description: "AI-assisted cybersecurity risk assessment tools that identify vulnerabilities and recommend appropriate security measures.",
                      icon: (
                        <svg className="w-10 h-10 text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      )
                    },
                    {
                      title: "Security Measures Implementation",
                      description: "Structured frameworks for implementing state-of-the-art technical and organizational security measures across your organization.",
                      icon: (
                        <svg className="w-10 h-10 text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      )
                    },
                    {
                      title: "Incident Response",
                      description: "Complete incident response framework with automated notification workflows and required reporting to competent authorities.",
                      icon: (
                        <svg className="w-10 h-10 text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      )
                    },
                    {
                      title: "Supply Chain Security",
                      description: "Tools for assessing and managing security risks in your supply chain, including vendor assessment templates and risk scoring.",
                      icon: (
                        <svg className="w-10 h-10 text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      )
                    },
                    {
                      title: "Regulatory Reporting",
                      description: "Automated generation of regulatory reports and documentation required by national competent authorities under NIS2.",
                      icon: (
                        <svg className="w-10 h-10 text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )
                    },
                    {
                      title: "Compliance Monitoring",
                      description: "Continuous monitoring of NIS2 compliance status with real-time dashboards and automatic alerts for potential compliance gaps.",
                      icon: (
                        <svg className="w-10 h-10 text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                      )
                    }
                  ].map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                    >
                      {feature.icon}
                      <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              {/* Sector Coverage section */}
              <div className="bg-white rounded-xl shadow-lg p-10 mb-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="text-center mb-10"
                >
                  <h2 className="text-3xl font-semibold mb-4">Complete Sector Coverage</h2>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    Our platform provides comprehensive compliance solutions for all sectors covered by NIS2.
                  </p>
                </motion.div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {[
                    "Energy",
                    "Transport",
                    "Banking",
                    "Financial Markets",
                    "Health",
                    "Drinking Water",
                    "Wastewater",
                    "Digital Infrastructure",
                    "ICT Service Management",
                    "Public Administration",
                    "Space",
                    "Research",
                    "Manufacturing",
                    "Digital Providers",
                    "Postal Services",
                    "Waste Management"
                  ].map((sector, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.03 }}
                      viewport={{ once: true }}
                      className="bg-gray-50 p-4 rounded-lg flex items-center"
                    >
                      <svg className="w-5 h-5 text-emerald-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">{sector}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              {/* Benefits section */}
              <div className="mb-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="text-center mb-10"
                >
                  <h2 className="text-3xl font-semibold mb-4">Benefits of Our NIS2 Solution</h2>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    Our platform delivers measurable improvements to your cybersecurity compliance process.
                  </p>
                </motion.div>
                
                <div className="grid md:grid-cols-2 gap-8">
                  {[
                    {
                      title: "70% Time Savings",
                      description: "Reduce time spent on NIS2 compliance tasks by up to 70% through automation and AI assistance."
                    },
                    {
                      title: "90% Faster Incident Reporting",
                      description: "Meet the 24-hour notification requirement with streamlined incident response workflows."
                    },
                    {
                      title: "55% Cost Reduction",
                      description: "Significantly reduce compliance costs by streamlining processes and reducing dependency on external consultants."
                    },
                    {
                      title: "83% Risk Reduction",
                      description: "Minimize cybersecurity risks through continuous monitoring and proactive threat identification."
                    }
                  ].map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex"
                    >
                      <div className="mr-4 flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                          <svg className="w-6 h-6 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                        <p className="text-gray-600">{benefit.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              {/* CTA section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl shadow-lg p-10 text-white text-center"
              >
                <h2 className="text-3xl font-semibold mb-4">Ready to Strengthen Your Cybersecurity Posture?</h2>
                <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
                  Join forward-thinking organizations that are enhancing their cybersecurity frameworks with our AI-powered NIS2 compliance platform.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100">
                    Schedule a Demo
                  </Button>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-emerald-700">
                    Contact Sales
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default NIS2;